import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '../services'
import { queryKeys } from '../lib/queryClient'

/**
 * Individual alert control item
 */
export interface AlertControl {
  alertType: string
  enabled: boolean
  [key: string]: any
}

/**
 * API response structure for getAlertControls
 *
 * @remarks
 * The API returns an array of alert controls directly, not wrapped in an object.
 * Each control represents a different alert type (e.g., 'SENSOR_ALERTS', 'TEST_MESSAGES')
 *
 * Example response: [{ alertType: 'SENSOR_ALERTS', enabled: true }, ...]
 */
export type GetAlertControlsResponse = AlertControl[]

/**
 * React Query hook for fetching global alert controls
 *
 * Cache Strategy:
 * - staleTime: 10 minutes (alert controls rarely change)
 * - gcTime: 30 minutes (keep in memory for extended period)
 * - Service Worker: CacheFirst with 30min expiration
 *
 * @param enabled - Whether the query should run (default: true)
 */
export function useAlertControls(enabled: boolean = true) {
  return useQuery({
    queryKey: queryKeys.alertControls,
    queryFn: async () => {
      const result = await api.getAlertControls()
      return result as GetAlertControlsResponse
    },
    staleTime: 10 * 60 * 1000, // 10 minutes - alert controls rarely change
    gcTime: 30 * 60 * 1000, // 30 minutes
    enabled,
    retry: 2,
    retryDelay: 1000,
  })
}

/**
 * Mutation hook for updating alert controls
 *
 * Uses optimistic updates for instant UI feedback
 * Automatically invalidates and refetches on success
 */
export function useUpdateAlertControls() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (settings: { alertType: string; enabled: boolean }) => {
      return await api.updateAlertControls(settings)
    },
    onMutate: async (newSettings) => {
      const queryKey = queryKeys.alertControls

      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey })

      // Snapshot previous value
      const previousControls = queryClient.getQueryData(queryKey)

      // Optimistically update cache - update matching alertType in array
      queryClient.setQueryData(queryKey, (old: GetAlertControlsResponse | undefined) => {
        if (!old) return old

        return old.map((control) =>
          control.alertType === newSettings.alertType
            ? { ...control, ...newSettings }
            : control
        )
      })

      // Return context for rollback on error
      return { previousControls, queryKey }
    },
    onError: (err, newSettings, context) => {
      // Rollback on error
      if (context?.previousControls) {
        queryClient.setQueryData(context.queryKey, context.previousControls)
      }
    },
    // Note: Removed onSuccess invalidation to prevent flickering
    // Optimistic update is sufficient for UI feedback
    // Data will be refreshed on next query refetch (10min staleTime)
  })
}
